import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import type {
  IRefreshRunDetail,
  IRefreshRunRow
} from '../../api/brokercheck-refresh-contracts';
import { brokerCheckRefreshApi } from '../../api/brokercheck-refresh-api';
import {
  RUN_COUNT_GROUPS,
  countLabel,
  isRunActive,
  orderedCounts,
  runStateChip,
  runTriggerLabel,
  type IRefreshClock,
  type RunCountGroup
} from '../../brokercheck-refresh';
import { AdminPage, LoadError, REFRESH_TABS, useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { refreshRunQueuePath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { Fact, Missing, PaneSection } from '../compliance/work-item-pane';
import { CancelRunButton, RunNoteLine, type IRunNote } from './run-writes';
import { RunProgress, RunStateChip, RunsZoneLine, useRefreshArea } from './runs-area';

/**
 * One BrokerCheck refresh run's record (SR-222, F10's R7, R8, R10) —
 * `/team/setup/brokercheck-refresh/runs/:runId`, the runs list beside it.
 *
 * **Three arms over one read**, the work item pane's: a line of text while it is out (never the
 * kit's skeleton, the split pane's rule), survey-api's refusal said, or the run. "No such run" is
 * a 404's sentence and an id that is not a GUID's, which the reader answers unsent (R10) — and,
 * for a run this pane had drawn, "no longer there", under what the write that found it said. A
 * later read that fails over a drawn run keeps it, under "Could not refresh".
 *
 * **The counts are survey-api's, in its report's order** (`RUN_COUNT_GROUPS` and `orderedCounts`,
 * the mirror of `BrokerCheckRefreshReport.cs`): answers, failures, IAPD's answers and failures
 * (SR-396 — not drawn where an older API sends none), then the work items raised; each key and its
 * count as sent, a known key in words with its code beside, an unknown one spelled, an empty group
 * "None". Nothing is added up here — the run keeps its counts in each number's own transaction,
 * and the record is survey-api's sum.
 *
 * **The work items it raised are the queue's**, not a second table here (F10's Q3): "Work items
 * raised" links to the compliance queues narrowed to the run, whole and per trigger.
 *
 * **A paused run says when it resumes** in the report's two sentences, off the record's
 * `nextSlot` and whether the schedule — read once by the area — is enabled.
 *
 * **Cancel's answer is adopted, not re-read** (R8): the row it answers is the run as cancelled,
 * laid over the record **as last read** — a "Read again" that landed while the cancellation was
 * out included, never the record as it was when Cancel was pressed; the list beside it is read
 * again. Nothing here polls (R9): "Read again"
 * reads the run by hand.
 */
export function AdminRefreshRunPage() {
  const { runId = '' } = useParams();
  // Keyed by the id: the split view keeps the route's element mounted when the list beside it
  // opens another run, and a record kept across runs would draw one under the other's address
  // (the hub's lesson, and the corrections').
  return <RunPane key={runId} runId={runId} />;
}

/** The read's answer — `found` null for no such run. */
interface IRead {
  found: Nullable<IRefreshRunDetail>;
}

function RunPane({ runId }: { runId: string }) {
  const location = useLocation();
  const rereadList = useListReread();
  const { clock, rereadSchedule } = useRefreshArea();
  const { data, error, reload, adopt } = useAdminData<IRead>(async (signal) => {
    try {
      return { found: await brokerCheckRefreshApi.run(runId, signal) };
    } catch (readError) {
      throw new Error(refusalMessage(readError, 'Could not read the run'));
    }
  });
  const [note, setNote] = React.useState<Nullable<IRunNote>>(null);
  // The record as last read, for Cancel's answer to be laid over (see the module's comment).
  const latest = React.useRef<Nullable<IRefreshRunDetail>>(null);
  if (data?.found) latest.current = data.found;
  const stateLine = React.useRef<HTMLDivElement>(null);

  const readAgain = React.useCallback(async () => {
    rereadList();
    void rereadSchedule();
    return reload();
  }, [reload, rereadList, rereadSchedule]);

  // A run's tab is told from another's by when it was made, in the schedule's zone (F11's R5).
  const made = data?.found && clock ? `, ${clock.instant(data.found.createdAt)}` : '';
  const frame = (children: React.ReactNode) => (
    <AdminPage
      title="BrokerCheck refresh run"
      documentTitle={`BrokerCheck refresh run${made}`}
      crumb="BrokerCheck refresh"
      tabs={REFRESH_TABS}
      actions={
        <Button type="button" size="sm" variant="outline" onClick={() => void readAgain()}>
          Read again
        </Button>
      }
    >
      {children}
    </AdminPage>
  );

  if (data === null || clock === null) {
    return frame(
      error ? (
        <LoadError message={error} />
      ) : (
        <p className="text-sm text-muted-foreground">Reading the run…</p>
      )
    );
  }

  if (data.found === null) {
    return frame(
      <div className="grid gap-6">
        {note && <RunNoteLine note={note} search={location.search} />}
        <Alert>
          <AlertTitle>No such run</AlertTitle>
          <AlertDescription>
            {latest.current === null
              ? 'No refresh run has this address — it may have been mistyped, or come from ' +
                'another stack’s page.'
              : 'The run this page showed is no longer there.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const run = data.found;

  const cancelled = (row: IRefreshRunRow) => {
    adopt({ found: { ...(latest.current ?? run), ...row } });
    setNote({
      tone: 'done',
      text:
        row.state === 'cancelled'
          ? 'Cancelled — the run stops where it was.'
          : `The cancellation came back with the run ` +
            `${runStateChip(row.state).label.toLowerCase()}.`
    });
    rereadList();
  };

  return frame(
    <div className="grid gap-6" data-run={run.id}>
      <RunsZoneLine />
      {error && (
        <LoadError message={`Could not refresh — showing the run as last read. ${error}`} />
      )}
      {note && <RunNoteLine note={note} search={location.search} />}

      <div
        ref={stateLine}
        tabIndex={-1}
        aria-label="Run state"
        className="flex flex-wrap items-center gap-3 outline-none"
      >
        <RunStateChip state={run.state} />
        <CancelRunButton
          runId={run.id}
          active={isRunActive(run.state)}
          focusAfter={() => stateLine.current?.focus()}
          onCancelled={cancelled}
          onSaid={setNote}
          reread={readAgain}
        />
      </div>
      {run.state === 'paused' && <Resumes run={run} clock={clock} />}

      <Facts run={run} clock={clock} />

      {RUN_COUNT_GROUPS.map((group) => {
        const counts = run[group];
        // IAPD's two groups are absent from an API before SR-396: nothing was counted, so no
        // group is drawn — "None" would say IAPD was asked and answered nothing.
        if (counts === undefined) return null;
        return (
          <Counts
            key={group}
            group={group}
            title={COUNT_TITLE[group]}
            counts={counts}
            queue={
              group === 'raised'
                ? (trigger) => refreshRunQueuePath(run.id, trigger)
                : undefined
            }
          />
        );
      })}
    </div>
  );
}

/** When a paused run goes on — the report's two sentences (R7). Whether the schedule is on is the
 *  area's read; without it, the sentence says the condition rather than a slot it cannot vouch
 *  for. */
function Resumes({ run, clock }: { run: IRefreshRunDetail; clock: IRefreshClock }) {
  const { schedule } = useRefreshArea();
  const next = clock.instant(run.nextSlot.startsAt);
  let text: string;
  if (schedule === null) {
    text =
      `Resumes at the next slot, ${next}, if the schedule is enabled — the schedule could not ` +
      'be read to say whether it is.';
  } else if (schedule.enabled) {
    text = `Resumes at the next slot, ${next}.`;
  } else {
    text = 'Resumes at the first slot after the schedule is enabled.';
  }
  return (
    <p className="text-sm" data-resumes>
      {text}
    </p>
  );
}

/** The run as its row and record say: what started it, its window, its times, how far it got. */
function Facts({ run, clock }: { run: IRefreshRunDetail; clock: IRefreshClock }) {
  const trigger = runTriggerLabel(run.trigger);
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      <Fact label="Trigger">
        {trigger}
        {trigger !== run.trigger && (
          <span className="ml-1.5 font-mono text-xs text-muted-foreground">{run.trigger}</span>
        )}
      </Fact>
      {run.trigger === 'manual' ? (
        <Fact label="Requested by">{run.requestedBy?.email ?? <Missing />}</Fact>
      ) : (
        <Fact label="Slot">
          {run.slot === null ? (
            <Missing />
          ) : run.windowEndsAt === null ? (
            clock.instant(run.slot)
          ) : (
            clock.window(run.slot, run.windowEndsAt)
          )}
        </Fact>
      )}
      <Fact label="Created">{clock.instant(run.createdAt)}</Fact>
      <Fact label="Started">
        {run.startedAt === null ? (
          <span className="text-muted-foreground">Not started</span>
        ) : (
          clock.instant(run.startedAt)
        )}
      </Fact>
      <Fact label="Finished">
        {run.finishedAt === null ? <Missing /> : clock.instant(run.finishedAt)}
      </Fact>
      <Fact label="Cancelled by">{run.cancelledBy?.email ?? <Missing />}</Fact>
      <Fact label="Progress">
        <RunProgress run={run} />
      </Fact>
      <Fact label="Holders refreshed">
        <span className="tabular-nums">{run.holdersRefreshed}</span>
      </Fact>
    </dl>
  );
}

/** Each group's heading. IAPD's are its own, beside BrokerCheck's (SR-396's Q1): a person's
 *  number is asked of both, and each registry's answers are counted apart. */
const COUNT_TITLE: Record<RunCountGroup, string> = {
  answers: 'Answers',
  failures: 'Failures',
  iapdAnswers: 'IAPD answers',
  iapdFailures: 'IAPD failures',
  raised: 'Work items raised'
};

/** One count group in the report's order: a key in words with its code beside, or as spelled.
 *  With `queue`, each count opens the queue at its key's items and the group links to them all. */
function Counts({
  group,
  title,
  counts,
  queue
}: {
  group: RunCountGroup;
  title: string;
  counts: Record<string, number>;
  queue?: (key?: string) => string;
}) {
  const rows = orderedCounts(group, counts);
  return (
    <PaneSection title={title}>
      <div data-run-counts={group}>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <dl className="grid max-w-md gap-1">
            {rows.map(([key, count]) => {
              const label = countLabel(group, key);
              return (
                <div
                  key={key}
                  data-count={key}
                  className="flex items-baseline justify-between gap-4 border-b border-border py-1 text-sm"
                >
                  <dt>
                    {label ?? <span className="font-mono text-xs">{key}</span>}
                    {label && (
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">{key}</span>
                    )}
                  </dt>
                  <dd className="tabular-nums">
                    {queue ? (
                      <Link
                        to={queue(key)}
                        aria-label={`Open the ${count} raised as ${label ?? key}`}
                        className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
                      >
                        {count}
                      </Link>
                    ) : (
                      count
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
        {queue && rows.length > 0 && (
          <Link
            to={queue()}
            className="mt-2 inline-block text-sm text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open every work item this run raised
          </Link>
        )}
      </div>
    </PaneSection>
  );
}
